import { useCallback, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/useToast";
import { threadsApi } from "@/lib/humhub-api/threads";
import { threadKeys } from "@/lib/queryKeys/threadKeys";

type NotificationVariables = {
  rootPostContentId: number;
  enabled: boolean;
};

type CacheSnapshot = {
  queryKey: readonly unknown[];
  previousEnabled: boolean | undefined;
  hadPreviousEnabled: boolean;
};

type ThreadMutationState = {
  generation: number;
  queue: Promise<void>;
  serverEnabled: boolean | undefined;
};

const sharedThreadMutationStates = new Map<number, ThreadMutationState>();
const sharedPendingRootPostContentIds = new Set<number>();
const pendingSubscribers = new Set<() => void>();
let pendingSnapshotVersion = 0;

const subscribeToPendingThreads = (listener: () => void) => {
  pendingSubscribers.add(listener);
  return () => pendingSubscribers.delete(listener);
};

const getPendingSnapshot = () => pendingSnapshotVersion;

const updatePendingThread = (rootPostContentId: number, pending: boolean) => {
  const changed = pending
    ? !sharedPendingRootPostContentIds.has(rootPostContentId)
    : sharedPendingRootPostContentIds.has(rootPostContentId);
  if (!changed) return;
  if (pending) {
    sharedPendingRootPostContentIds.add(rootPostContentId);
  } else {
    sharedPendingRootPostContentIds.delete(rootPostContentId);
  }
  pendingSnapshotVersion += 1;
  pendingSubscribers.forEach((listener) => {
    listener();
  });
};

type CacheUpdate = {
  data: unknown;
  changed: boolean;
  found: boolean;
  previousEnabled: boolean | undefined;
  hadPreviousEnabled: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isThreadListKey = (queryKey: readonly unknown[]) =>
  queryKey[0] === threadKeys.all[0] && queryKey[1] === "list";

const isThreadDetailKey = (
  queryKey: readonly unknown[],
  rootPostContentId: number,
) =>
  queryKey[0] === threadKeys.all[0] &&
  queryKey[1] === "detail" &&
  queryKey[2] === rootPostContentId;

const isFavoritePostsKey = (queryKey: readonly unknown[]) =>
  queryKey[0] === "favoritePosts" && queryKey[1] === "list";

const isStreamKey = (queryKey: readonly unknown[]) =>
  (queryKey[0] === "posts" &&
    (queryKey[1] === "list" || queryKey[1] === "detail")) ||
  (queryKey[0] === "pinnedPosts" && queryKey[1] === "list") ||
  isFavoritePostsKey(queryKey);

const isRelevantKey = (
  queryKey: readonly unknown[],
  rootPostContentId: number,
) =>
  isThreadListKey(queryKey) ||
  isThreadDetailKey(queryKey, rootPostContentId) ||
  isStreamKey(queryKey);

const isThreadListItem = (
  value: Record<string, unknown>,
  rootPostContentId: number,
) =>
  value.is_following === true &&
  value.root_post_content_id === rootPostContentId;

const isStreamItem = (
  value: Record<string, unknown>,
  rootPostContentId: number,
) => {
  if (value.is_following !== true) return false;
  if (value.id === rootPostContentId) return true;
  return isRecord(value.content) && value.content.id === rootPostContentId;
};

const mapResultData = (
  value: unknown,
  updateItem: (item: Record<string, unknown>) => Record<string, unknown>,
) => {
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      if (!isRecord(item)) return item;
      const updated = updateItem(item);
      changed ||= updated !== item;
      return updated;
    });
    return changed ? next : value;
  }
  if (!isRecord(value)) return value;
  if (Array.isArray(value.results)) {
    const results = mapResultData(value.results, updateItem);
    return results === value.results ? value : { ...value, results };
  }
  if (Array.isArray(value.pages)) {
    let changed = false;
    const pages = value.pages.map((page) => {
      const updated = mapResultData(page, updateItem);
      changed ||= updated !== page;
      return updated;
    });
    return changed ? { ...value, pages } : value;
  }
  return updateItem(value);
};

const updateNotificationInCache = (
  queryKey: readonly unknown[],
  data: unknown,
  rootPostContentId: number,
  enabled: boolean | undefined,
  expectedEnabled?: boolean,
): CacheUpdate => {
  let found = false;
  let previousEnabled: boolean | undefined;
  let hadPreviousEnabled = false;
  let capturedPreviousEnabled = false;
  const updateItem = (item: Record<string, unknown>) => {
    const favoriteContent =
      isFavoritePostsKey(queryKey) && isRecord(item.content)
        ? item.content
        : null;
    const target = favoriteContent ?? item;
    const matches =
      isThreadListKey(queryKey) ||
      isThreadDetailKey(queryKey, rootPostContentId)
        ? isThreadListItem(target, rootPostContentId)
        : isStreamItem(target, rootPostContentId);
    if (!matches) return item;

    found = true;
    const currentEnabled =
      typeof target.notification_enabled === "boolean"
        ? target.notification_enabled
        : undefined;
    if (!capturedPreviousEnabled) {
      capturedPreviousEnabled = true;
      previousEnabled = currentEnabled;
      hadPreviousEnabled = Object.hasOwn(target, "notification_enabled");
    }
    if (
      (expectedEnabled !== undefined && currentEnabled !== expectedEnabled) ||
      currentEnabled === enabled
    ) {
      return item;
    }
    const updated = { ...target };
    if (enabled === undefined) {
      delete updated.notification_enabled;
    } else {
      updated.notification_enabled = enabled;
    }
    return favoriteContent ? { ...item, content: updated } : updated;
  };

  const next = isThreadDetailKey(queryKey, rootPostContentId)
    ? isRecord(data) && isThreadListItem(data, rootPostContentId)
      ? updateItem(data)
      : data
    : mapResultData(data, updateItem);
  return {
    data: next,
    changed: next !== data,
    found,
    previousEnabled,
    hadPreviousEnabled,
  };
};

export const useThreadNotification = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  useSyncExternalStore(subscribeToPendingThreads, getPendingSnapshot);

  const setNotificationEnabled = useCallback(
    async ({ rootPostContentId, enabled }: NotificationVariables) => {
      const state = sharedThreadMutationStates.get(rootPostContentId) ?? {
        generation: 0,
        queue: Promise.resolve(),
        serverEnabled: undefined,
      };
      const generation = state.generation + 1;
      state.generation = generation;
      sharedThreadMutationStates.set(rootPostContentId, state);
      updatePendingThread(rootPostContentId, true);

      const ownsGeneration = () => state.generation === generation;
      const snapshots: CacheSnapshot[] = [];
      const prepareOptimisticUpdate = async () => {
        const queries = queryClient.getQueryCache().findAll({
          predicate: (query) =>
            isRelevantKey(query.queryKey, rootPostContentId),
        });
        await Promise.all(
          queries.map((query) =>
            queryClient.cancelQueries({
              queryKey: query.queryKey,
              exact: true,
            }),
          ),
        );
        if (!ownsGeneration()) return;

        for (const query of queryClient.getQueryCache().findAll({
          predicate: (item) => isRelevantKey(item.queryKey, rootPostContentId),
        })) {
          const update = updateNotificationInCache(
            query.queryKey,
            query.state.data,
            rootPostContentId,
            enabled,
          );
          if (state.serverEnabled === undefined && update.found) {
            state.serverEnabled = update.previousEnabled;
          }
          if (!update.changed) continue;
          snapshots.push({
            queryKey: query.queryKey,
            previousEnabled: update.previousEnabled,
            hadPreviousEnabled: update.hadPreviousEnabled,
          });
          queryClient.setQueryData(query.queryKey, update.data);
        }
      };

      const optimisticUpdate = prepareOptimisticUpdate();
      const request = state.queue.then(async () => {
        await optimisticUpdate;
        const response = await threadsApi.setNotificationEnabled(
          rootPostContentId,
          enabled,
        );
        state.serverEnabled = response.notification_enabled;
        return response;
      });
      const queueTail = request.then(
        () => undefined,
        () => undefined,
      );
      state.queue = queueTail;

      try {
        const response = await request;
        if (ownsGeneration()) {
          for (const query of queryClient.getQueryCache().findAll({
            predicate: (item) =>
              isRelevantKey(item.queryKey, rootPostContentId),
          })) {
            const update = updateNotificationInCache(
              query.queryKey,
              query.state.data,
              rootPostContentId,
              response.notification_enabled,
            );
            if (update.changed) {
              queryClient.setQueryData(query.queryKey, update.data);
            }
          }
        }
        return response;
      } catch (error) {
        if (ownsGeneration()) {
          for (const query of queryClient.getQueryCache().findAll({
            predicate: (item) =>
              isRelevantKey(item.queryKey, rootPostContentId),
          })) {
            const snapshot = snapshots.find(
              (item) => item.queryKey === query.queryKey,
            );
            if (state.serverEnabled === undefined && !snapshot) continue;
            const restoredEnabled =
              state.serverEnabled ??
              (snapshot?.hadPreviousEnabled
                ? snapshot.previousEnabled
                : undefined);
            const update = updateNotificationInCache(
              query.queryKey,
              query.state.data,
              rootPostContentId,
              restoredEnabled,
              enabled,
            );
            if (update.changed) {
              queryClient.setQueryData(query.queryKey, update.data);
            }
          }
          toast({ variant: "error", description: t("threads.actionError") });
        }
        throw error;
      } finally {
        if (ownsGeneration()) {
          void queryClient.invalidateQueries({ queryKey: threadKeys.lists() });
          void queryClient.invalidateQueries({
            queryKey: threadKeys.detail(rootPostContentId),
          });
          void queryClient.invalidateQueries({
            predicate: (query) => isStreamKey(query.queryKey),
          });
          updatePendingThread(rootPostContentId, false);
          if (
            sharedThreadMutationStates.get(rootPostContentId) === state &&
            state.queue === queueTail
          ) {
            sharedThreadMutationStates.delete(rootPostContentId);
          }
        }
      }
    },
    [queryClient, t],
  );

  return {
    setNotificationEnabled,
    isPending: sharedPendingRootPostContentIds.size > 0,
    isPendingThread: (rootPostContentId: number) =>
      sharedPendingRootPostContentIds.has(rootPostContentId),
    pendingRootPostContentId:
      sharedPendingRootPostContentIds.values().next().value ?? null,
  };
};
